import React, { useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import { postCardIntro, postCardIntroText } from "../lib/text";
import {
  POST_HTML_PURIFY_CONFIG,
  sanitizeIframeHook,
  sanitizeAnchorAttrHook,
  flagSmallTextParagraphs,
} from "../lib/sanitize";
import { hydrateHtmlEmbeds } from "../lib/htmlEmbed";
import { htmlHasPoll, splitHtmlByPolls } from "../lib/pollEmbed";
import InteractivePoll from "./InteractivePoll";

/**
 * Renders a preview-card teaser for a post.
 *
 *   - If `post.custom_intro` was set by the admin, render its HTML
 *     (sanitised) so inline formatting like <strong>/<em>/<a> survives.
 *     Any "Insert HTML embed" blocks (raw HTML/iframe, same block type
 *     as the body editor) are hydrated in-place after mount — same
 *     pipeline `RichBodyWithCarousels` uses for the full post body, so
 *     embeds on card previews render/execute exactly like the real
 *     published page instead of showing an empty placeholder. A Fan
 *     Poll embedded in the intro is swapped for the real
 *     `<InteractivePoll>` widget the same way, instead of rendering as
 *     an inert, invisible placeholder div.
 *   - Otherwise render the auto plain-text excerpt from the start of
 *     `post.body`, truncated to `maxLen` chars.
 *
 * The container is a <div> (not <p>) so card markup stays valid even
 * when the custom intro contains paragraph-level tags.
 *
 * Pass `plainTextOnly` for compact list-style cards (e.g. the archive
 * page) that must only ever show a short, truncated, tag-stripped
 * excerpt — no rich HTML, no images, no custom-intro blocks rendered
 * in full. This bypasses the HTML/custom_intro branch entirely.
 */
export default function CardIntro({
  post,
  maxLen = 220,
  className = "",
  plainTextOnly = false,
  ...rest
}) {
  const { html, text } = plainTextOnly
    ? { html: "", text: postCardIntroText(post, maxLen) }
    : postCardIntro(post, maxLen);
  const rootRef = useRef(null);

  const safeHtml = useMemo(() => {
    if (!html || plainTextOnly) return "";
    DOMPurify.addHook("uponSanitizeElement", sanitizeIframeHook);
    DOMPurify.addHook("uponSanitizeAttribute", sanitizeAnchorAttrHook);
    try {
      return flagSmallTextParagraphs(DOMPurify.sanitize(html, POST_HTML_PURIFY_CONFIG));
    } finally {
      DOMPurify.removeHook("uponSanitizeElement");
      DOMPurify.removeHook("uponSanitizeAttribute");
    }
  }, [html, plainTextOnly]);

  useEffect(() => {
    if (!plainTextOnly && rootRef.current) hydrateHtmlEmbeds(rootRef.current);
  }, [safeHtml, plainTextOnly]);

  if (safeHtml) {
    // Strip `line-clamp-N` from the className when rendering custom
    // intro HTML so the admin's layout (floated images, headings,
    // size-marked text) shows in full. The card height becomes
    // content-driven — admins can keep their custom intro short if
    // they want a compact card. Without this, webkit's line-clamp
    // chops floated images and tall headings.
    const finalClass = `card-intro ${className.replace(/\bline-clamp-\d+\b/g, "").trim()}`;

    if (htmlHasPoll(safeHtml)) {
      return (
        <div ref={rootRef} className={finalClass} {...rest}>
          {splitHtmlByPolls(safeHtml).map((seg, i) =>
            seg.type === "poll" ? (
              <InteractivePoll key={`poll-${i}`} pollId={seg.id} className="my-4" />
            ) : (
              <div key={`h-${i}`} dangerouslySetInnerHTML={{ __html: seg.value }} />
            ),
          )}
        </div>
      );
    }

    return (
      <div
        ref={rootRef}
        className={finalClass}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
        {...rest}
      />
    );
  }

  if (!text) return null;

  return (
    <div className={className} {...rest}>
      {text}
    </div>
  );
}
